import { useEffect, useState } from 'react'
import moment from 'moment'

import { RecurlyPayment } from 'api'
import { useRecurly } from 'lib/recurly'
import { useCheckout, useUpdate } from 'features/checkout/hooks'
import { useUser } from 'hooks/use-user'
import Button from 'components/ui/button'
import { cn } from 'utils/classMerge'
import { Mixpanel, MIXPANEL_PROPS } from 'utils/mixpanel'
import { isFreeTrialUsed } from 'utils/freeVersionBlock'
import { NOT_TRIAL_PLANS } from 'utils/consts.mjs'
import { getAddonPrice, getCurrencyAndRegion } from 'utils/payment'
import { translate } from 'utils/translation'
import { useContextSave } from 'hooks/use-context-save'

import { CHECKOUT_SUBMIT_TYPE } from '../form/consts'
import { RecurlyContext } from '../../providers/index'

import { ApplePayLogo } from './logo'

const AIW_ADDON_NAME = {
  addon_aiw_blogger: 'aiw_plan_blogger',
  addon_aiw_shakespeare: 'aiw_plan_business',
  addon_aiw_business: 'aiw_plan_shakespeare',
}

const isDev = process.env.NODE_ENV === 'development'

declare global {
  interface Window {
    ApplePaySession?: unknown
  }
}

interface ApplePayProps {
  className?: string
  source?: string
  checkoutSuccessSource: string
  blacklistedPaymentMethod?: boolean
}

interface PaymentData {
  payment: {
    recurlyToken: RecurlyPayment
  }
}

interface RecurlyApplePayInstance {
  begin: () => void
  ready: (callback: () => void) => void
  on: (event: string, callback: (err: unknown) => void) => void
  onCancel?: () => void
}

interface RecurlyApplePayConfig {
  country: string
  currency: string
  paymentRequest?: {
    total: { label: string; amount: string }
    lineItems?: Array<{
      label: string
      amount: string
      paymentTiming?: string
      recurringPaymentStartDate?: Date
      recurringPaymentIntervalUnit?: string
      recurringPaymentIntervalCount?: number
      recurringPaymentEndDate?: Date
    }>
    recurringPaymentRequest?: {
      paymentDescription: string
      regularBilling: {
        label: string
        amount: string
        paymentTiming: string
        recurringPaymentStartDate: Date
        recurringPaymentIntervalUnit: string
        recurringPaymentIntervalCount: number
      }
      trialBilling?: {
        label: string
        amount: string
        paymentTiming: string
        recurringPaymentIntervalUnit: string
        recurringPaymentIntervalCount: number
        recurringPaymentEndDate: Date
      }
    }
  }
  callbacks: {
    onPaymentAuthorized: (paymentData: PaymentData) => void
  }
}

export const ApplePay = ({
  className,
  source,
  checkoutSuccessSource,
  blacklistedPaymentMethod = false,
}: ApplePayProps) => {
  const context = useContextSave(RecurlyContext)

  const {
    currentSubscription,
    setIsLoading,
    isLoading,
    tier,
    interval,
    submitType,
    hasAIWAddon,
    addonDetail,
  } = context
  const { handleCheckout } = useCheckout()
  const { handleUpdate } = useUpdate()
  const user = useUser()
  const recurly = useRecurly()

  const [applePay, setApplePay] = useState<RecurlyApplePayInstance | null>(null)
  const [isReady, setIsReady] = useState(false)

  const disabled = isLoading || !applePay || isDev || !window.ApplePaySession

  const handlePaymentAuthorization = ({ payment }: PaymentData) => {
    setIsLoading(true)

    const handlePaymentCallBack =
      submitType === CHECKOUT_SUBMIT_TYPE.update ? handleUpdate : handleCheckout
    handlePaymentCallBack({
      token: payment.recurlyToken,
      paymentMethod: MIXPANEL_PROPS.BUTTON_NAME.values.APPLEPAY,
      checkoutSuccessSource,
      noTrial: blacklistedPaymentMethod,
    })
  }

  const getTotalAmount = ({
    subscriptionAmount,
    addonPrice,
    hasTrial,
  }: {
    subscriptionAmount: string
    addonPrice: string | null
    hasTrial: boolean
  }) => {
    if (hasTrial || submitType === CHECKOUT_SUBMIT_TYPE.update) return '0.00'
    return (
      Number(subscriptionAmount || '0') + Number(addonPrice || '0')
    ).toFixed(2)
  }

  const getTotalLabel = () => {
    if (submitType === CHECKOUT_SUBMIT_TYPE.update)
      return translate('update_billing_info')
    return translate(`dropdown_${tier}_${interval}`)
  }

  const getSubscriptionBillingInterval = (hasTrial: boolean) => {
    const endOfTrial = moment().add(7, 'day').toDate()
    return {
      paymentTiming: 'recurring',
      recurringPaymentStartDate: hasTrial ? endOfTrial : moment().toDate(),
      recurringPaymentIntervalUnit: (() => {
        if (currentSubscription?.plan === 'yearly') return 'year'
        if (currentSubscription?.plan === 'lifetime') return ''
        return 'month'
      })(),
      recurringPaymentIntervalCount: 1,
    }
  }

  const buildPaymentRequest = ({
    subscriptionAmount,
    addonPrice,
    hasTrial,
    totalAmount,
    totalLabel,
  }: {
    subscriptionAmount: string
    addonPrice: string | null
    hasTrial: boolean
    totalAmount: string
    totalLabel: string
  }) => {
    const subscriptionBillingInterval = getSubscriptionBillingInterval(hasTrial)
    const lineItemsBillingInterval = {
      paymentTiming: 'recurring',
      recurringPaymentStartDate: moment().toDate(),
      recurringPaymentIntervalUnit: 'month',
      recurringPaymentIntervalCount: 1,
    }
    const trialInterval = {
      paymentTiming: 'recurring',
      recurringPaymentIntervalUnit: 'day',
      recurringPaymentIntervalCount: 7,
      recurringPaymentEndDate:
        subscriptionBillingInterval.recurringPaymentStartDate,
    }

    const labelText = `${totalLabel}${
      hasTrial ? ` ${translate('free_trial_only')}` : ''
    }`
    const labelAddon = addonDetail?.code
      ? translate(
          AIW_ADDON_NAME[addonDetail?.code as keyof typeof AIW_ADDON_NAME],
        )
      : ''

    return {
      total: {
        label: labelAddon
          ? translate('plan_plus_addon', labelText, labelAddon)
          : labelText,
        amount: totalAmount,
      },
      ...(addonPrice &&
        submitType !== CHECKOUT_SUBMIT_TYPE.update &&
        addonDetail && {
          lineItems: [
            {
              label: translate(
                AIW_ADDON_NAME[addonDetail.code as keyof typeof AIW_ADDON_NAME],
              ),
              amount: addonPrice,
              ...lineItemsBillingInterval,
            },
          ],
        }),
      ...(currentSubscription &&
        currentSubscription.plan !== 'lifetime' &&
        submitType !== CHECKOUT_SUBMIT_TYPE.update && {
          recurringPaymentRequest: {
            paymentDescription: 'My Ubersuggest Subscription',
            regularBilling: {
              label: totalLabel,
              amount: subscriptionAmount,
              ...subscriptionBillingInterval,
            },
            ...(hasTrial && {
              trialBilling: {
                label: translate('free_trial_only'),
                amount: '0.00',
                ...trialInterval,
              },
            }),
          },
        }),
    }
  }

  useEffect(() => {
    if (!user || !currentSubscription || isDev || !window.ApplePaySession)
      return

    const trialUsed = isFreeTrialUsed()
    const hasTrial =
      !trialUsed &&
      !NOT_TRIAL_PLANS.includes(interval) &&
      !hasAIWAddon &&
      !blacklistedPaymentMethod

    const subscriptionAmount =
      currentSubscription.unitPrice?.replace(/\D/g, '') || '0'

    const { planCode } = getCurrencyAndRegion(user.countryCode)

    const addonPrice =
      hasAIWAddon && addonDetail && currentSubscription
        ? `${getAddonPrice(
            addonDetail,
            currentSubscription.currency,
            planCode,
          )}`
        : null

    const totalAmount = getTotalAmount({
      subscriptionAmount,
      addonPrice,
      hasTrial,
    })
    const totalLabel = getTotalLabel()
    const paymentRequest = buildPaymentRequest({
      subscriptionAmount,
      addonPrice,
      hasTrial,
      totalAmount,
      totalLabel,
    })

    const applePayConfig: RecurlyApplePayConfig = {
      country: user.countryCode || 'US',
      currency: currentSubscription.currency || 'USD',
      paymentRequest,
      callbacks: { onPaymentAuthorized: handlePaymentAuthorization },
    }

    const applePayInstance = recurly.ApplePay(applePayConfig)
    applePayInstance.ready(() => {
      applePayInstance.onCancel = () => setIsLoading(false)
      setIsReady(true)
    })
    applePayInstance.on('error', (err: Error) => {
      setIsLoading(false)
      console.error('Apple Pay error', err)
    })
    applePayInstance.on('cancel', () => {
      setIsLoading(false)
    })

    setApplePay(applePayInstance)

    return () => {
      setApplePay(null)
      setIsReady(false)
      applePayInstance.on('error', () => null)
      applePayInstance.on('cancel', () => null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.countryCode,
    currentSubscription?.currency,
    currentSubscription?.unitPrice,
  ])

  if (!isReady && !isDev) return null

  return (
    <Button
      disabled={disabled}
      variant="legacy-box"
      className={cn(
        'disabled:hover:tw-border-red tw-m-0 tw-flex tw-h-fit tw-min-w-[120px] tw-items-center tw-justify-center !tw-rounded-md tw-px-4 tw-py-[11px] tw-normal-case',
        className,
      )}
      onClick={() => {
        if (!isReady || isLoading) return
        setIsLoading(true)
        Mixpanel.trackPaymentButtonClicked({
          tier,
          planInterval: interval,
          buttonName: MIXPANEL_PROPS.BUTTON_NAME.values.APPLEPAY,
          source,
        })
        applePay?.begin()
      }}
    >
      <ApplePayLogo
        colorClass={disabled ? 'tw-fill-zinc-200' : 'tw-fill-black'}
      />
    </Button>
  )
}
